<template>
  <CardProjectActivityBase
    title="Low 5 Achievement"
    v-model:projectId="projectId"
    :loadingData="loading"
    :hasData="hasData"
    loadingText="Loading performance data..."
    noDataText="No low achievements found for this project."
    selectPrompt="Select a project to see Low 5 achievements"
    emptyIcon="icon-trending-down"
    @selected="fetchLowData"
    @print="printReport"
  >
    <template #controls>
      <div class="btn-group btn-group-sm">
        <button 
          @click="activeTab = 'activity'" 
          class="btn" 
          :class="activeTab === 'activity' ? 'btn-primary text-white' : 'btn-outline-primary'"
        >
          Activity Wise
        </button>
        <button 
          @click="activeTab = 'team'" 
          class="btn" 
          :class="activeTab === 'team' ? 'btn-primary text-white' : 'btn-outline-primary'"
        >
          Team Wise
        </button>
      </div>
    </template>

    <div v-if="activeTab === 'activity'" class="table-responsive scrollable-table">
      <table class="table table-sm table-bordered table-striped mb-0">
        <thead>
          <tr class="text-dark bg-white">
            <th class="text-left py-1 border-bottom-0">Activity</th>
            <th class="text-right py-1 border-bottom-0">Target</th>
            <th class="text-right py-1 border-bottom-0">Already Achieved</th>
            <th class="text-right py-1 border-bottom-0">Balance</th>
            <th class="text-right py-1 border-bottom-0">Achievement %</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in achievements.activities" :key="item.id" class="align-middle">
            <td class="activity-name font-bold">{{ item.name }}</td>
            <td class="text-right">{{ commaFormat(item.total_target) }}</td>
            <td class="text-right">{{ commaFormat(item.total_achievement) }}</td>
            <td class="text-right">{{ commaFormat(item.balance) }}</td>
            <td class="text-right font-bold text-danger">{{ item.achievement_percent }}%</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="activeTab === 'team'" class="table-responsive scrollable-table">
      <table class="table table-sm table-bordered table-striped mb-0">
        <thead>
          <tr class="text-dark bg-white">
            <th class="text-left py-1 border-bottom-0">Team Member</th>
            <th class="text-right py-1 border-bottom-0">Target</th>
            <th class="text-right py-1 border-bottom-0">Already Achieved</th>
            <th class="text-right py-1 border-bottom-0">Balance</th>
            <th class="text-right py-1 border-bottom-0">Achievement %</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in achievements.teams" :key="index" class="align-middle">
            <td class="font-bold">{{ item.name }}</td>
            <td class="text-right">{{ commaFormat(item.target) }}</td>
            <td class="text-right">{{ commaFormat(item.already_achieved) }}</td>
            <td class="text-right">{{ commaFormat(item.balance) }}</td>
            <td class="text-right font-bold text-danger">{{ item.achievement_percent }}%</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="text-right mt-1 pb-1">
      <a @click="seeDetails" class="text-primary font-bold cursor-pointer small">
        See Details.. <i class="feather icon-arrow-right"></i>
      </a>
    </div>
  </CardProjectActivityBase>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router'
import CardProjectActivityBase from "./CardProjectActivityBase.vue";
import handleProjects from "@/services/modules/procurement/project";
import { useSharedProjectList } from "@/services/modules/procurement/useSharedProjectList";
import { commaFormat } from "@/services/utils/global";
import useDate from "@/services/utils/day";
import dashboardCardPdfPrinter from "@/services/utils/dashboardCardPdfPrinter";

const { fetchTopAndLowAchievements } = handleProjects();
const { projects } = useSharedProjectList();
const { firstDateOfMonth, lastDateOfMonth } = useDate();

const route = useRoute();
const router = useRouter();
const store = useStore();
const showError = inject('showError');
const { printCardReport } = dashboardCardPdfPrinter();

const projectId = ref(null);
const achievements = ref({ activities: [], teams: [] });
const loading = ref(false);
const activeTab = ref('activity');

const props = defineProps({
  page: Object,
  itemKey: String,
  statisticsItem: Object,
  timeRange: Array
})

const hasData = computed(() => {
  if (activeTab.value === 'activity') return achievements.value.activities.length > 0;
  return achievements.value.teams.length > 0;
});

const fetchLowData = async () => {
  if (!projectId.value) return;
  
  loading.value = true;
  try {
    const query = `?company_id=${route.params.companyId}&start_date=${firstDateOfMonth}&end_date=${lastDateOfMonth}&type=low`;
    const res = await fetchTopAndLowAchievements(projectId.value, query);
    
    if (!res.status) {
      achievements.value = { activities: [], teams: [] };
      showError(res.message || 'Failed to fetch data');
      return;
    }

    achievements.value = res.data;
  } catch (error) {
    showError('Something went wrong');
  } finally {
    loading.value = false;
  }
};

const seeDetails = () => {
  const routeData = router.resolve({
    name: 'narrative-report',
    params: { 
      ...route.params, 
      pageId: props.page ? props.page.id : null
    }
  });
  window.open(routeData.href, '_blank');
};

const printReport = () => {
  if (!projectId.value) {
    showError('Please select a project first');
    return;
  }
  
  const selectedProject = projects.value.find(p => p.id === projectId.value);
  const company = store.getters['company/getCompany'];
  const isActivity = activeTab.value === 'activity';
  
  const headers = [
    { text: isActivity ? 'Activity' : 'Team Member', width: '*' },
    { text: 'Target', width: 'auto', alignment: 'right' },
    { text: 'Already Achieved', width: 'auto', alignment: 'right' },
    { text: 'Balance', width: 'auto', alignment: 'right' },
    { text: 'Achievement %', width: 'auto', alignment: 'right' }
  ];
  
  const rawData = isActivity ? achievements.value.activities : achievements.value.teams;
  const bodyData = rawData.map(item => [
    { text: item.name, bold: true },
    { text: commaFormat(isActivity ? item.total_target : item.target), alignment: 'right' },
    { text: commaFormat(isActivity ? item.total_achievement : item.already_achieved), alignment: 'right' },
    { text: commaFormat(item.balance), alignment: 'right' },
    { text: `${item.achievement_percent}%`, alignment: 'right', bold: true, color: '#ea5455' }
  ]);
  
  printCardReport(
    `Low 5 Achievement Report (${isActivity ? 'Activity Wise' : 'Team Wise'})`,
    headers,
    bodyData,
    company,
    `Project: ${selectedProject ? selectedProject.name : ''}`
  );
};
</script>

<style scoped>
.scrollable-table {
  max-height: 400px;
  overflow-y: auto;
}
.activity-name {
  text-align: left;
}
.font-bold {
  font-weight: 600;
}
.cursor-pointer {
  cursor: pointer;
}
.text-primary {
  color: #7367f0 !important;
}
.text-danger {
  color: #ea5455 !important;
}
.btn-primary {
  background-color: #7367f0 !important;
  border-color: #7367f0 !important;
}
.btn-outline-primary {
  color: #7367f0 !important;
  border-color: #7367f0 !important;
}
.btn-outline-primary:hover {
  background-color: #7367f0 !important;
  color: #fff !important;
}
.border-bottom-0 {
    border-bottom: 0 !important;
}
</style>
